import React, { useEffect, useRef, useState } from 'react';
import { NavigationTabsType, NavigationTabsView, NavigationTabsViewProps } from './NavigationTabs.view';

export type NavigationTabsControllerProps = Omit<
  NavigationTabsViewProps,
  'tabItems' | 'moreItems' | 'moreRef' | 'open' | 'setOpen'
> & {
  containerWidth: number;
  actionElementWidth: number;
  TabList: NavigationTabsType[];
};

type TabDataValuesType = {
  index: number;
  width: number;
};

export const NavigationTabsController = (props: NavigationTabsControllerProps): JSX.Element => {
  const ref = useRef<HTMLUListElement | null>(null);
  const [renderedCount, setRenderedCount] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [splitIndex, setSplitIndex] = useState<number>(props.TabList.length);
  const [tabDataValues, setTabDataValues] = useState<Array<TabDataValuesType>>([]);

  // Build our state based array of TabDataValueTypes for future reference
  // We only do this on first render
  const buildTabDataValues = () => {
    const tabMenuItems = ref.current?.querySelectorAll('li:not(#more-navigation)') as NodeListOf<HTMLLIElement>;
    // Make sure that tabDataValues array is cleared/cleaned before building out
    setTabDataValues(() => []);

    tabMenuItems.forEach((item, index) => {
      const itemData = { index: index, width: item.offsetWidth };
      setTabDataValues(currentTabs => {
        return [...currentTabs, itemData];
      });
    });
  };

  const calculateSplitIndex = () => {
    let widthSum = 0;

    tabDataValues.every(tabData => {
      widthSum += tabData.width;

      const isPastWidthLimit = widthSum > props.containerWidth - props.actionElementWidth;

      if (isPastWidthLimit) {
        setSplitIndex(() => {
          return tabData.index - 1;
        });

        return false;
      }

      return true;
    });

    setRenderedCount(count => count + 1);
  };

  useEffect(() => {
    setSplitIndex(() => {
      return props.TabList.length;
    });
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [props.containerWidth]);

  useEffect(() => {
    // Only do this on first time render
    if (renderedCount < 1) {
      buildTabDataValues();
    }

    calculateSplitIndex();
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [splitIndex, props.containerWidth]);

  const tabItems: NavigationTabsType[] = [];
  const moreItems: NavigationTabsType[] = [];

  props.TabList.forEach((item, index) => {
    if (index < splitIndex) {
      tabItems.push(item);
    } else {
      moreItems.push(item);
    }
  });

  const viewProps: NavigationTabsViewProps = {
    ...props,
    tabItems,
    moreItems,
    open,
    setOpen
  };

  return <NavigationTabsView {...viewProps} ref={ref} />;
};
